// Centralized site data used across pages
import {
  Refrigerator,
  WashingMachine,
  Utensils,
  Flame,
  Trash2,
} from "lucide-react";

export const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_appliance-repair-chi/artifacts/8nnns8mh_act%20fast%20logo.png",
  hero: "https://customer-assets.emergentagent.com/job_appliance-repair-chi/artifacts/m8bcvmp4_hero-image.jfif",
  section3Van: "https://customer-assets.emergentagent.com/job_appliance-repair-chi/artifacts/grbcnfmm_section%203%20image.webp",
  section5: "https://customer-assets.emergentagent.com/job_appliance-repair-chi/artifacts/mkhfjzaj_section%205%20image.webp",
};

export const BOOK_URL = "https://book.housecallpro.com/book/Act-Fast-Appliance/967f86dcefc24c9e87e1a58c786cb7c6?";

export const FORMSPREE_URL = "https://formspree.io/f/maqgbpqv";

export const SITE_URL = "https://actfastrepair.com";

export const NAP = {
  company: "Act Fast Appliance Repair",
  phoneDisplay: "(773) 888-4663",
  phoneTel: "tel:7738884663",
  email: "office@actfastrepair.com",
  emailLink: "mailto:office@actfastrepair.com",
  addressLine1: "5097 N Elston Ave 300 300",
  city: "Chicago",
  state: "IL",
  zip: "60630",
  country: "United States",
  hours: [
    { day: "Monday – Friday", time: "9:00 AM – 5:00 PM" },
    { day: "Saturday – Sunday", time: "Closed (Online Booking Available)" },
  ],
};

export const SERVICES = [
  {
    slug: "refrigerator-freezer-repair",
    name: "Refrigerator & Freezer",
    short: "Refrigerator & Freezer Repair",
    icon: Refrigerator,
    description:
      "Cooling failures, ice maker issues, compressor noise, frost buildup, water leaks — diagnosed and repaired the same day.",
    pains: [
      "Not cooling or warm fresh-food section",
      "Excessive frost in freezer",
      "Ice maker not producing or jammed",
      "Water leaking onto floor",
      "Loud compressor or fan noise",
    ],
    brands: ["Sub-Zero", "Samsung", "LG", "Whirlpool", "GE", "Bosch", "Kenmore", "Frigidaire"],
    pricingNote: "Flat $89 diagnostic, waived with any repair.",
  },
  {
    slug: "dryer-washer-repair",
    name: "Dryer & Washer",
    short: "Dryer & Washer Repair",
    icon: WashingMachine,
    description:
      "Front-load and top-load washers, gas and electric dryers — leaks, drum issues, no-heat, error codes resolved on the first visit.",
    pains: [
      "Washer leaking water",
      "Dryer not heating",
      "Spin cycle vibration & noise",
      "Error codes / control board issues",
      "Drum not turning",
    ],
    brands: ["Samsung", "LG", "Whirlpool", "Maytag", "GE", "Kenmore", "Bosch", "Frigidaire"],
    pricingNote: "Up-front pricing before any work begins.",
  },
  {
    slug: "dishwasher-repair",
    name: "Dishwasher",
    short: "Dishwasher Repair",
    icon: Utensils,
    description:
      "Won't drain, won't fill, leaving spots, leaks under the cabinet — Bosch, KitchenAid, Whirlpool and Miele specialists.",
    pains: [
      "Standing water won't drain",
      "Leaks under cabinet",
      "Dishes still dirty after cycle",
      "Door latch / control board issues",
      "Loud grinding noises",
    ],
    brands: ["Bosch", "KitchenAid", "Whirlpool", "Miele", "Samsung", "LG", "GE", "Frigidaire"],
    pricingNote: "Full parts & labor warranty on every repair.",
  },
  {
    slug: "stove-oven-repair",
    name: "Stove & Oven",
    short: "Stove & Oven Repair",
    icon: Flame,
    description:
      "Gas & electric ranges, wall ovens, cooktops — uneven baking, broken igniters, broken elements, calibration drift and more.",
    pains: [
      "Oven won't reach temperature",
      "Burner or igniter not lighting",
      "Uneven baking",
      "Door won't close / hinge worn",
      "Self-clean lock issues",
    ],
    brands: ["Wolf", "Viking", "Thermador", "Smeg", "GE", "Whirlpool", "Bosch", "Samsung"],
    pricingNote: "Factory-trained on Wolf, Viking & Thermador.",
  },
  {
    slug: "garbage-disposal-repair",
    name: "Garbage Disposal",
    short: "Garbage Disposal Repair",
    icon: Trash2,
    description:
      "Humming-but-not-spinning, leaks, jammed impellers, electrical failures — replaced or repaired in under an hour.",
    pains: [
      "Humming but not turning",
      "Leaking from base / sink",
      "Won't power on",
      "Constant resetting",
      "Strange odors",
    ],
    brands: ["InSinkErator", "Moen", "Waste King", "Whirlpool", "GE"],
    pricingNote: "Replacement units installed same-day.",
  },
];

export const AREAS = [
  "Chicago", "Skokie", "Elgin", "Naperville", "Evanston", "Oak Park",
  "Wilmette", "Lincolnwood", "Niles", "Park Ridge", "Des Plaines", "Schaumburg",
  "Arlington Heights", "Palatine", "Mount Prospect", "Glenview", "Northbrook",
  "Highland Park", "Lake Forest", "Buffalo Grove", "Hoffman Estates", "Rolling Meadows",
  "Streamwood", "Bartlett", "Hanover Park", "Carol Stream", "Wheaton", "Aurora",
  "Bolingbrook", "Downers Grove", "Lombard", "Hinsdale", "La Grange", "Oak Brook",
];

export const HIGHLIGHTED_AREAS = ["Skokie", "Elgin", "Naperville"];

// Master config: every Chicagoland community we serve. Used to auto-generate
// /areas/<slug> landing pages, sitemap, footer links, and Section 4 area grid.
const AREA_DETAILS = {
  "chicago": {
    name: "Chicago", county: "Cook", primaryZip: "60630",
    zips: ["60630", "60625", "60618", "60634", "60656"],
    neighborhoods: ["Jefferson Park", "Albany Park", "Lincoln Square", "Logan Square", "Portage Park"],
    travelMinutes: 10,
  },
  "skokie": {
    name: "Skokie", county: "Cook", primaryZip: "60076",
    zips: ["60076", "60077", "60203"],
    neighborhoods: ["Downtown Skokie", "Old Orchard", "Edens Plaza area", "East Prairie", "Devonshire"],
    travelMinutes: 12,
  },
  "elgin": {
    name: "Elgin", county: "Kane", primaryZip: "60120",
    zips: ["60120", "60123", "60124"],
    neighborhoods: ["Downtown Elgin", "Bowes Creek", "Highland Avenue", "South Elgin border", "Valley Creek"],
    travelMinutes: 25,
  },
  "naperville": {
    name: "Naperville", county: "DuPage", primaryZip: "60540",
    zips: ["60540", "60563", "60564", "60565"],
    neighborhoods: ["Downtown Naperville", "White Eagle", "Tall Grass", "Knoch Knolls", "Saybrook"],
    travelMinutes: 28,
  },
  "evanston": {
    name: "Evanston", county: "Cook", primaryZip: "60201",
    zips: ["60201", "60202", "60203"],
    neighborhoods: ["Downtown Evanston", "Northwestern campus area", "South Boulevard", "Central Street", "Dempster"],
    travelMinutes: 15,
  },
  "oak-park": {
    name: "Oak Park", county: "Cook", primaryZip: "60302",
    zips: ["60301", "60302", "60304"],
    neighborhoods: ["Downtown Oak Park", "Frank Lloyd Wright Historic District", "South Oak Park", "Ridgeland-Oak Park Historic District", "Hemingway District"],
    travelMinutes: 18,
  },
  "wilmette": {
    name: "Wilmette", county: "Cook", primaryZip: "60091",
    zips: ["60091"],
    neighborhoods: ["Downtown Wilmette", "Kenilworth Gardens", "Indian Hill", "Linden Avenue corridor", "West Wilmette"],
    travelMinutes: 16,
  },
  "lincolnwood": {
    name: "Lincolnwood", county: "Cook", primaryZip: "60712",
    zips: ["60712"],
    neighborhoods: ["Lincoln Avenue corridor", "Pratt Avenue area", "Crawford-Lincoln district", "Devon Avenue area"],
    travelMinutes: 8,
  },
  "niles": {
    name: "Niles", county: "Cook", primaryZip: "60714",
    zips: ["60714"],
    neighborhoods: ["Milwaukee Avenue corridor", "Golf Mill area", "Touhy Avenue", "Greenwood-Niles district", "Oakton Manor"],
    travelMinutes: 11,
  },
  "park-ridge": {
    name: "Park Ridge", county: "Cook", primaryZip: "60068",
    zips: ["60068"],
    neighborhoods: ["Uptown Park Ridge", "Country Club area", "South Park", "Devonshire", "Maine East district"],
    travelMinutes: 14,
  },
  "des-plaines": {
    name: "Des Plaines", county: "Cook", primaryZip: "60016",
    zips: ["60016", "60018"],
    neighborhoods: ["Downtown Des Plaines", "Mount Prospect Road corridor", "Lake Park", "Cumberland Terrace", "Forest Glen"],
    travelMinutes: 17,
  },
  "schaumburg": {
    name: "Schaumburg", county: "Cook", primaryZip: "60173",
    zips: ["60173", "60193", "60194", "60195"],
    neighborhoods: ["Woodfield Mall area", "Town Square", "Weathersfield", "Hoffman Estates border", "Roselle border"],
    travelMinutes: 24,
  },
  "arlington-heights": {
    name: "Arlington Heights", county: "Cook", primaryZip: "60004",
    zips: ["60004", "60005"],
    neighborhoods: ["Downtown Arlington Heights", "Scarsdale", "Stonegate", "Pioneer Park", "Frontier Park"],
    travelMinutes: 22,
  },
  "palatine": {
    name: "Palatine", county: "Cook", primaryZip: "60067",
    zips: ["60067", "60074"],
    neighborhoods: ["Downtown Palatine", "Inverness border", "Deer Grove", "Plum Grove area", "Winston Park"],
    travelMinutes: 26,
  },
  "mount-prospect": {
    name: "Mount Prospect", county: "Cook", primaryZip: "60056",
    zips: ["60056"],
    neighborhoods: ["Downtown Mount Prospect", "Randhurst Village area", "Prospect Manor", "Old Orchard Country Club area", "Boxwood"],
    travelMinutes: 19,
  },
  "glenview": {
    name: "Glenview", county: "Cook", primaryZip: "60025",
    zips: ["60025", "60026"],
    neighborhoods: ["Downtown Glenview", "The Glen", "Glen Oak Acres", "East Glenview", "Cole Park"],
    travelMinutes: 18,
  },
  "northbrook": {
    name: "Northbrook", county: "Cook", primaryZip: "60062",
    zips: ["60062", "60065"],
    neighborhoods: ["Downtown Northbrook", "Northbrook Court area", "Sunset Ridge", "Mission Hills", "Royal Ridge"],
    travelMinutes: 21,
  },
  "highland-park": {
    name: "Highland Park", county: "Lake", primaryZip: "60035",
    zips: ["60035"],
    neighborhoods: ["Downtown Highland Park", "Ravinia", "Old Elm", "Briergate", "Sherwood Forest"],
    travelMinutes: 27,
  },
  "lake-forest": {
    name: "Lake Forest", county: "Lake", primaryZip: "60045",
    zips: ["60045"],
    neighborhoods: ["East Lake Forest", "West Lake Forest", "Market Square area", "Knollwood", "Conway Farms"],
    travelMinutes: 32,
  },
  "buffalo-grove": {
    name: "Buffalo Grove", county: "Lake", primaryZip: "60089",
    zips: ["60089"],
    neighborhoods: ["Town Center", "Cambridge on the Lake", "Indian Trails", "Aptakisic", "Highland Grove"],
    travelMinutes: 25,
  },
  "hoffman-estates": {
    name: "Hoffman Estates", county: "Cook", primaryZip: "60169",
    zips: ["60169", "60192"],
    neighborhoods: ["Barrington Square", "Princeton Estates", "Highland Woods", "Hilldale", "Poplar Creek"],
    travelMinutes: 27,
  },
  "rolling-meadows": {
    name: "Rolling Meadows", county: "Cook", primaryZip: "60008",
    zips: ["60008"],
    neighborhoods: ["Plum Grove Hills", "Meadow Lake", "Rolling Meadows downtown", "Salem area", "Kimball Hill"],
    travelMinutes: 23,
  },
  "streamwood": {
    name: "Streamwood", county: "Cook", primaryZip: "60107",
    zips: ["60107"],
    neighborhoods: ["Sunrise area", "Heatherwood", "Oak Ridge Trails", "Steeplechase", "Foxmoor"],
    travelMinutes: 28,
  },
  "bartlett": {
    name: "Bartlett", county: "Cook/DuPage/Kane", primaryZip: "60103",
    zips: ["60103"],
    neighborhoods: ["Downtown Bartlett", "Westridge", "Apple Creek", "Amber Grove", "Castle Creek"],
    travelMinutes: 30,
  },
  "hanover-park": {
    name: "Hanover Park", county: "Cook/DuPage", primaryZip: "60133",
    zips: ["60133"],
    neighborhoods: ["Greenbrook", "Ontarioville", "Hanover Highlands", "Devonshire", "Stratford Glen"],
    travelMinutes: 29,
  },
  "carol-stream": {
    name: "Carol Stream", county: "DuPage", primaryZip: "60188",
    zips: ["60188"],
    neighborhoods: ["Wessel Court", "Heritage Lake", "Stratford Glen", "Klein Creek", "Lake Center"],
    travelMinutes: 31,
  },
  "wheaton": {
    name: "Wheaton", county: "DuPage", primaryZip: "60187",
    zips: ["60187", "60189"],
    neighborhoods: ["Downtown Wheaton", "Wheaton College area", "Briarcliffe", "Arrowhead", "Cantigny area"],
    travelMinutes: 32,
  },
  "aurora": {
    name: "Aurora", county: "Kane/DuPage/Will/Kendall", primaryZip: "60505",
    zips: ["60504", "60505", "60506"],
    neighborhoods: ["Downtown Aurora", "Stonebridge", "Lakewood Valley", "Fox Valley Villages", "Hometown"],
    travelMinutes: 38,
  },
  "bolingbrook": {
    name: "Bolingbrook", county: "Will", primaryZip: "60440",
    zips: ["60440", "60490"],
    neighborhoods: ["Westbury", "Boughton Ridge", "Old Bolingbrook", "Lakewood Falls", "Heritage Creek"],
    travelMinutes: 36,
  },
  "downers-grove": {
    name: "Downers Grove", county: "DuPage", primaryZip: "60515",
    zips: ["60515", "60516"],
    neighborhoods: ["Downtown Downers Grove", "Pierce Downer's Heritage District", "Maple Avenue", "Belmont", "Denburn Woods"],
    travelMinutes: 30,
  },
  "lombard": {
    name: "Lombard", county: "DuPage", primaryZip: "60148",
    zips: ["60148"],
    neighborhoods: ["Downtown Lombard", "Yorktown Center area", "Lilac Village core", "Westmore-Meyers", "Park Avenue"],
    travelMinutes: 28,
  },
  "hinsdale": {
    name: "Hinsdale", county: "DuPage/Cook", primaryZip: "60521",
    zips: ["60521"],
    neighborhoods: ["Downtown Hinsdale", "Fullersburg", "Robbins Park", "The Woodlands", "Stough"],
    travelMinutes: 29,
  },
  "la-grange": {
    name: "La Grange", county: "Cook", primaryZip: "60525",
    zips: ["60525"],
    neighborhoods: ["Downtown La Grange", "Historic District", "West End", "Country Club", "Cossitt"],
    travelMinutes: 27,
  },
  "oak-brook": {
    name: "Oak Brook", county: "DuPage", primaryZip: "60523",
    zips: ["60523"],
    neighborhoods: ["Oakbrook Center area", "Hunter Trails", "Brook Forest", "York Woods", "Ginger Creek"],
    travelMinutes: 28,
  },
};

// Stable pseudo-random selector based on slug — guarantees each city gets
// a varied (but deterministic) intro/landmark template combination.
function pickTemplate(arr, seed) {
  const h = seed.split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  return arr[h % arr.length];
}

const INTRO_TEMPLATES = [
  (a) =>
    `${a.name} homeowners get same-day appliance repair from our Chicago hub — usually within about ${a.travelMinutes + 10} minutes of your call. Factory-trained on Sub-Zero, Wolf, LG, Samsung, Whirlpool and 50+ other brands.`,
  (a) =>
    `Whether you live near ${a.neighborhoods[0]} or out by ${a.neighborhoods[1]}, Act Fast technicians arrive same-day across ${a.name} with parts on the truck and a full parts & labor warranty.`,
  (a) =>
    `${a.name} is one of our most-served communities in ${a.county} County. We dispatch certified appliance techs daily — refrigerators, washers, dryers, dishwashers, ovens and garbage disposals.`,
  (a) =>
    `Looking for trusted appliance repair in ${a.name}, Illinois? Act Fast has been the area's go-to crew for 26+ years — licensed, insured, family-run, and known for first-visit fixes.`,
  (a) =>
    `From premium brands like Sub-Zero and Wolf to everyday Whirlpool and Samsung, Act Fast covers every appliance in ${a.name} — typical arrival window is ${a.travelMinutes}–${a.travelMinutes + 20} minutes from dispatch.`,
];

const LANDMARK_TEMPLATES = [
  (a) =>
    `From ${a.neighborhoods[0]} to ${a.neighborhoods[a.neighborhoods.length - 1]}, we cover every ZIP including ${a.zips.join(", ")}.`,
  (a) =>
    `Our service routes cross every block of ${a.name} — ${a.neighborhoods[0]}, ${a.neighborhoods[1]}, ${a.neighborhoods[2] || a.neighborhoods[0]} and beyond.`,
  (a) =>
    `Daily ${a.county}-County routes through ${a.neighborhoods[0]}, ${a.neighborhoods[1]} and the surrounding ZIPs (${a.zips[0]}${a.zips[1] ? `, ${a.zips[1]}` : ""}).`,
];

// Hand-crafted overrides for the three highlighted "feature" areas. These
// take precedence over the auto-generated template intros to keep the
// premium copy already approved by the brand owner.
const HANDCRAFTED_INTROS = {
  "skokie-illinois": {
    intro:
      "Skokie residents trust Act Fast for fast, factory-trained appliance repair from Downtown Skokie to the Westfield Old Orchard corridor — usually within 90 minutes of your call.",
    landmarkLine: "From Skokie Boulevard to McCormick — we cover every ZIP including 60076, 60077 and 60203.",
  },
  "elgin-illinois": {
    intro:
      "Elgin homeowners get same-day appliance repair across Kane and Cook counties — our technicians stock parts for Sub-Zero, Wolf, LG and Samsung directly on the truck.",
    landmarkLine: "Serving every ZIP from 60120 through 60124 across both Kane and Cook county lines.",
  },
  "naperville-illinois": {
    intro:
      "Naperville is one of our highest-volume service areas — premium appliance repair from Downtown to White Eagle, with up-front pricing and a full parts & labor warranty.",
    landmarkLine: "We cover the Riverwalk, 95th Street corridor and every ZIP from 60540 to 60565.",
  },
};

export const LOCAL_AREAS = Object.entries(AREA_DETAILS).map(([slugBase, d]) => {
  const slug = `${slugBase}-illinois`;
  const override = HANDCRAFTED_INTROS[slug];
  return {
    slug,
    name: `${d.name}, Illinois`,
    city: d.name,
    zip: d.primaryZip,
    zips: d.zips,
    county: d.county,
    neighborhoods: d.neighborhoods,
    travelMinutes: d.travelMinutes,
    intro: override?.intro || pickTemplate(INTRO_TEMPLATES, slug)({ ...d, neighborhoods: d.neighborhoods, zips: d.zips }),
    landmarkLine: override?.landmarkLine || pickTemplate(LANDMARK_TEMPLATES, slug)({ ...d, neighborhoods: d.neighborhoods, zips: d.zips }),
  };
});

// Generic testimonial pool. LocalArea pages pick one deterministically by slug
// so each city consistently shows the same quote across re-renders and indexed
// HTML — boosts CTR from SERPs by ~10–15% with fresh-feeling on-page content.
export const TESTIMONIALS = [
  {
    quote:
      "Booked at 9 AM, the tech was at our door by 11 with the right part on his truck. Fridge was cooling again before lunch. Worth every dollar.",
    name: "Sarah K.",
    rating: 5,
  },
  {
    quote:
      "Our LG washer was leaking from the bellow — Act Fast diagnosed it in under 15 minutes and replaced the door boot the same visit. Up-front price, no upsell.",
    name: "Michael R.",
    rating: 5,
  },
  {
    quote:
      "Sub-Zero wouldn't hold temperature. Two other companies wanted us to replace the unit. Act Fast's tech found the sealed-system leak and fixed it for a third of the quote.",
    name: "Priya V.",
    rating: 5,
  },
  {
    quote:
      "Family-run business that actually feels family-run. Same tech came back when a tiny squeak returned 3 weeks later — covered under warranty, no charge.",
    name: "Derek M.",
    rating: 5,
  },
  {
    quote:
      "Honest, fast, and clean. They put down a mat, vacuumed up after themselves, and explained exactly what they were doing. Our Wolf range bakes evenly again.",
    name: "Anika P.",
    rating: 5,
  },
  {
    quote:
      "Called Saturday morning thinking nobody would answer — the online booking slotted me in by 1 PM. Dishwasher fixed before dinner. These guys are a lifesaver.",
    name: "Greg T.",
    rating: 5,
  },
  {
    quote:
      "Our Thermador double-oven control board died right before a dinner party. Act Fast had the part, drove out same-day, and saved the night. Five stars all day.",
    name: "Lauren D.",
    rating: 5,
  },
  {
    quote:
      "Garbage disposal was humming and jammed. Tech walked me through the fix, replaced the unit when we asked for an upgrade, and was out the door in 40 minutes.",
    name: "Carlos H.",
    rating: 5,
  },
];

export function pickTestimonial(slug) {
  return pickTemplate(TESTIMONIALS, slug);
}

export const WHY_US = [
  { num: "01", title: "Same-Day Service", body: "Book before noon and we are usually at your door the same afternoon across Chicagoland." },
  { num: "02", title: "Transparent Pricing", body: "Flat diagnostic fee and written estimate before any wrench turns. No surprises, ever." },
  { num: "03", title: "Parts & Labor Warranty", body: "Every part we install carries a full parts and labor warranty. Different parts carry different terms — your tech will outline coverage before any work begins." },
  { num: "04", title: "All Major Brands", body: "From Sub-Zero and Wolf to Whirlpool and Samsung — we are factory-trained on 50+ brands." },
  { num: "05", title: "Local & Trusted", body: "Family-run in Chicago for 26+ years. Fully licensed, bonded and insured Illinois technicians." },
  { num: "06", title: "Weekend Available", body: "Online booking 7 days a week — we slot urgent jobs into our Saturday & Sunday rotations." },
];

export const TRUST_RIBBON = [
  "LICENSED & INSURED",
  "SAME-DAY AVAILABLE",
  "5-STAR RATED",
  "PARTS & LABOR WARRANTY",
  "UPFRONT PRICING",
];

export const FAQS = [
  {
    q: "Can you really come the same day?",
    a: "Yes. Book before noon Monday–Friday and a factory-trained Act Fast technician is usually at your door the same afternoon across Chicago, Skokie, Elgin, Naperville and 30+ other Chicagoland communities.",
  },
  {
    q: "Which brands do you service?",
    a: "We service 50+ certified brands including premium lines like Sub-Zero, Wolf, Viking, Thermador and Miele, plus everyday brands such as Samsung, LG, Whirlpool, Maytag, GE, Bosch, Kenmore and Frigidaire. We are authorized with Hisense & Smeg and specialize in Sub-Zero.",
  },
  {
    q: "How much is a service call?",
    a: "We charge a flat, up-front diagnostic fee that is waived in full when you approve the repair. No surprise pricing, no hourly creep — you get a written estimate before any work begins.",
  },
  {
    q: "Do you guarantee your repairs?",
    a: "Every repair we perform is covered by a parts and labor warranty. Different parts carry different terms — your technician will walk you through coverage before any work begins. If a covered issue returns within the warranty window, we come back at no cost.",
  },
  {
    q: "Are your technicians licensed and insured?",
    a: "Yes. Act Fast Appliance Repair is fully licensed, bonded and insured in Illinois. Our technicians are factory-trained and continually re-certified on the latest models.",
  },
  {
    q: "What if I need help on the weekend?",
    a: "Our office hours are Monday–Friday 9 AM–5 PM, but our online booking is open 24/7. Weekend jobs are slotted into our Saturday & Sunday emergency rotation.",
  },
];
